import * as moment from 'moment';
import * as Cookies from 'js-cookie/src/js.cookie.js';

import { ActHttpService, ContentTypes, HeaderTypes } from '../act-http/act-http.service';
import { EncryptionType, HttpMethod } from '../../model/constant';

import { ActToken } from '../authentication/authentication.service';
import { AuthenticationService } from 'app/services/authentication/authentication.service';
import { EncryptRequestPayload } from 'app/services/user/payload/encrypt-request.payload';
import { Injectable } from '@angular/core';
import { LoginApiResponseModel } from 'app/model/login/login-api-response.model';
import { LoginResponseModel } from '../../model/login/login-response.model';
import { Observable } from 'rxjs/Rx';
import { Response } from '@angular/http';
import { UserModel } from '../../model/user.model';
import { UtilitiesService } from 'app/services/utilities/utilities.service';
import { environment } from '../../../environments/environment';
import { AppConfigService } from '../app-config/app-config.service';
import { domain } from 'environments/properties';

@Injectable()
export class AffiliateService {

  constructor(
    private actHttp: ActHttpService,
    private utilitiesService: UtilitiesService,
    private authenticationService: AuthenticationService
  ) { }

  getUrlAuthenRequest(companyCode: string): Observable<any> {
    return this.actHttp
      .rawPost(environment.endpoint.getUrlAuthenRequest.replace(':affiliate', companyCode), { companyCode: companyCode })
      .map((res: Response) => res.json());
  }

  saveTokenAndUserInfo$(loginResponse: LoginResponseModel): Observable<boolean> {
    try {
      this.authenticationService.saveToken(loginResponse);
      const actToken = this.authenticationService.getToken();
      const userFromStorage = this.authenticationService.getUserInfo();
      return this.authenticationService.saveUserFeature(actToken, userFromStorage, AuthenticationService.features);
    } catch (error) {
      return Observable.of(false);
    }
  }

  createLoginResponse(token, user, companyCode): LoginResponseModel {
    const actToken = new ActToken(token.accessToken, token.refreshToken);
    const actUser = <UserModel>user;
    actUser.companyCode = companyCode;
    const loginResponse = new LoginResponseModel();
    loginResponse.user = actUser;
    loginResponse.token = actToken;
    return loginResponse;
  }

  /* **************** SAML Protocol **************** */
  loginWithSAML(loginResponse: LoginResponseModel): Observable<boolean> {
    return this.saveTokenAndUserInfo$(loginResponse);
  }

  logoutIntegrate(email: string, companyCode: string): Observable<Response> {
    const payload = new EncryptRequestPayload();
    payload.username = email;
    payload.timestamp = moment().unix();
    const options = this.actHttp.getRequestOptions(HeaderTypes.AUTHORIZATION_RANDOM_STR, ContentTypes.JSON, email, JSON.stringify(payload));
    return this.actHttp
      .rawPost(environment.endpoint.logoutAffiliate.replace(':affiliate', companyCode), JSON.stringify(payload), options)
      .map((res: Response) => res.json());
  }

  decodeSAMLFromURL(encryptUserInfo: string, companyCode: string): LoginResponseModel {
    const result = JSON.parse(this.utilitiesService.base64Decoder(decodeURIComponent(encryptUserInfo)));
    const { token, user } = result;
    return this.createLoginResponse(token, user, companyCode);
  }

  /* **************** API Protocol **************** */
  loginWithAPI(apiResponse: LoginApiResponseModel, formData): Observable<boolean> {
    const { username, password } = formData || { username: undefined, password: undefined };
    const {
      httpMethod, endPoint, appKey, companyCode,
      userNameField, passwordField, encryptionType
    } = apiResponse || new LoginApiResponseModel();

    // verify arguments
    const isValidArgs = [
      username, password, httpMethod, endPoint, appKey,
      userNameField, passwordField, encryptionType, companyCode,
    ].filter(args => args === undefined).length === 0;
    if (!isValidArgs) {
      return Observable.of(false);
    }

    let requestLogin: Observable<Response>;
    let params = {};
    params[userNameField] = username;
    params[passwordField] = this.encryptPasswordWithType(encryptionType, password);
    switch (httpMethod) {
      case HttpMethod.POST: {
        // TODO: update post method later
        const url = `${endPoint}`;
        requestLogin = this.actHttp.rawPost(url, params);
        break;
      }

      case HttpMethod.GET:
      default: {
        params = { ...params, key: appKey };
        const url = `${endPoint}${this.utilitiesService.objectToQueryString(params)}`;
        let requestEndpoint = environment.endpoint.loginAffiliate.replace(':affiliate', companyCode);
        requestEndpoint = `${requestEndpoint}?data=` + this.utilitiesService.base64Encoder(url);
        requestLogin = this.actHttp.rawGet(requestEndpoint);
        break;
      }
    }

    return requestLogin.map((res: Response) => res.json().data)
      .flatMap(({ token }) => this.getUserWithConsumeToken$(token, companyCode));
  }

  getUserWithConsumeToken$(token, companyCode): Observable<boolean> {
    try {
      if (!token || !companyCode) {
        throw new Error('affiliate token or companyCode not found');
      }
      const url = environment.endpoint.getAffiliateConsume.replace(':affiliate', companyCode);
      const params = { data: token };
      return this.actHttp.rawPost(url, params)
        .map(res => res.json().data)
        .flatMap(f => {
          this.authenticationService.saveSsoToken(f.token);
          return this.authenticationService.login(companyCode, f.clientId);
        });
    } catch (error) {
      return Observable.throw(new Error('Get user info with consume token fail'));
    } finally {
      this.removeAffiliateState();
    }
  }

  logoutWithAPI() {
    // TODO: implement logout by api protocol
  }

  encryptPasswordWithType(encryptionType, password): string {
    let encryptedPassword = '';
    switch (encryptionType) {
      case EncryptionType.TRUE_CUSTOM:
        const random2digit = this.utilitiesService.randomStringWithLength(2);
        const random4digit = this.utilitiesService.randomStringWithLength(4);
        const encryptPass = this.utilitiesService.base64Encoder(password);
        encryptedPassword = `${random2digit}${encryptPass}${random4digit}`;
        break;

      case EncryptionType.BASE64:
      default:
        encryptedPassword = this.utilitiesService.base64Encoder(password);
        break;
    }
    return encryptedPassword;
  }

  decodeAPIFromURL(encryptData: string, requireFields = LoginApiResponseModel.requireFields()): LoginApiResponseModel {
    const decryptData = JSON.parse(this.utilitiesService.base64Decoder(decodeURIComponent(encryptData)));
    const loginApi: LoginApiResponseModel = (<LoginApiResponseModel>decryptData);
    const amountOfRequired = requireFields.length;
    const validFields = requireFields.filter(key => loginApi[key] !== undefined && loginApi[key] !== '');
    return validFields.length === amountOfRequired ? loginApi : undefined;
  }

  saveAffiliateState(state: string) {
    Cookies.set(AppConfigService.stateAffCookieName, this.utilitiesService.encodeToCookie(state), { domain: domain });
  }

  getAffiliateState() {
    return this.utilitiesService.decodeFromCookie(Cookies.get(AppConfigService.stateAffCookieName));
  }

  removeAffiliateState() {
    Cookies.remove(AppConfigService.stateAffCookieName, { domain: domain });
  }

}



// WEBPACK FOOTER //
// ./src/app/services/affiliate/affiliate.service.ts